import {
  CANADA_POST,
  COURIERSPLEASE,
  DEUTSCHE_POST,
  DHL_EXPRESS,
  FASTWAY_AUSTRALIA,
  SENDLE,
  UPS,
  USPS,
} from '~/constants/carriers';
import { Country } from '~/types/Address';
import Carrier from '~/types/Carrier';
import { StoreName } from '~/types/Store';

export enum OnboardingSteps {
  WELCOME = 'WELCOME',
  INFORMATION = 'INFORMATION',
  PREFERENCES = 'PREFERENCES',
  ORDERS = 'ORDERS',
}

export enum OnboardingLocalStorageKeys {
  ONBOARDING_LAST_COMPLETED_STEP = 'shippo_onboarding_last_completed_step',
  ONBOARDING_CURRENT_STEP = 'shippo_onboarding_current_step',
  ONBOARDING_ACTIVE = 'shippo_onboarding_active',
  ONBOARDING_BUSINESS_TYPE = 'shippo_onboarding_business_type',
  ONBOARDING_BUSINESS_NEED = 'shippo_onboarding_business_need',
  ONBOARDING_SHOW_WELCOME_MESSAGE = 'shippo_onboarding_show_welcome',
  ONBOARDING_ORDER_MECHANISM = 'shippo_onboarding_order_mechanism',
  ONBOARDING_ACTIVE_STORE = 'shippo_onboarding_active_store',
  ONBOARDING_USER_COUNTRY = 'shippo_onboarding_user_country',
  ONBOARDING_PLAN = 'shippo_onboarding_plan',
  ONBOARDING_COMPLETED = 'shippo_onboarding_completed',
}

export enum BusinessType {
  NONE = 'none',
  BUSINESS = 'business',
  PERSONAL = 'personal',
  ADVANCED = 'partner',
}

export enum MonthlyVolume {
  OCCASIONALLY = 'I ship occasionally (under 10 packages a month)',
  LITE = '10 - 24 packages a month',
}

export const NUMBER_OF_PROGRESS_METER_STEPS = 4;

// "What best describes your business needs?" values
export enum BusinessNeed {
  // @NOTE: none should be default and personal will not have a business need
  NONE = 'none',
  'Shipping Goods' = 'Shipping goods that you sell',
  'Shipping Free Items' = 'Shipping free items (gifts, swag, documents, etc)',
  'Developer' = 'Shipping goods via API (for developers)',
  '3PL' = '3PL looking for a shipping partner',
  XMS = 'Logistics provider looking for a shipping partner',
  Warehouse = 'Warehouse looking for a shipping partner',
  'eCommerce Platform' = 'Building shipping for an eCommerce platform',
  Marketplace = 'Building shipping for a marketplace',
  'Seeking 3PL / Inventory Partner' = 'Business looking for a 3PL or inventory storage partner',
  'Seeking WMS' = 'Warehouse looking for a warehouse management solution',
  Other = 'Other',
}

export const getEnumKeyByEnumValue = <T extends Record<string, string>>(
  myEnum: T,
  enumValue: string,
): string => {
  let keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
  return keys.length > 0 ? keys[0] : '';
};

export const getMasterAccountDetail = (
  country: Maybe<Country>,
): {
  hasMasterAccount: boolean;
  masterCopy: string | null;
  carriers: Carrier[];
} => {
  let hasMasterAccount = false;
  let masterCopy: string | null = null;
  let carriers: Carrier[] = [];

  switch (country?.name) {
    case 'United States':
      hasMasterAccount = true;
      masterCopy = 'onboarding:newOnboarding.preferences.usCopy';
      carriers = [USPS, UPS, DHL_EXPRESS];
      break;
    case 'Canada':
      hasMasterAccount = true;
      masterCopy = 'onboarding:newOnboarding.preferences.canadaCopy';
      carriers = [CANADA_POST];
      break;
    case 'Australia':
      hasMasterAccount = true;
      masterCopy = 'onboarding:newOnboarding.preferences.ausCopy';
      carriers = [SENDLE, FASTWAY_AUSTRALIA, COURIERSPLEASE];
      break;
    case 'Germany':
      hasMasterAccount = true;
      masterCopy = 'onboarding:newOnboarding.preferences.germanyCopy';
      carriers = [DEUTSCHE_POST];
      break;
  }

  return {
    hasMasterAccount: hasMasterAccount,
    masterCopy: masterCopy,
    carriers: carriers,
  };
};

export const BusinessNeeds = {
  [BusinessType.NONE]: [BusinessNeed.NONE],
  [BusinessType.BUSINESS]: [
    BusinessNeed['Shipping Goods'],
    BusinessNeed['Shipping Free Items'],
    BusinessNeed['Developer'],
  ],
  [BusinessType.PERSONAL]: [BusinessNeed.NONE],
  [BusinessType.ADVANCED]: [
    BusinessNeed['3PL'],
    BusinessNeed.XMS,
    BusinessNeed.Warehouse,
    BusinessNeed['eCommerce Platform'],
    BusinessNeed.Marketplace,
    BusinessNeed.Developer,
    BusinessNeed['Seeking 3PL / Inventory Partner'],
    BusinessNeed['Seeking WMS'],
    BusinessNeed.Other,
  ],
};

export enum OrdersMechanism {
  MANUAL = 'manual',
  STORE = 'store',
  CSV = 'csv',
}

export interface NavigationItem {
  label: string;
  route: string;
}

export enum ViewKey {
  welcome = 'welcome',
  info = 'info',
  preferences = 'preferences',
  orders = 'orders',
  api = 'api',
  getting_started = 'getting_started',
  shipping_details = 'shipping_details',
  pro_plan = 'pro_plan',
}

export interface ViewComponents {
  [ViewKey.info]: ViewComponent[];
  [ViewKey.preferences]: ViewComponent[];
  [ViewKey.getting_started]: ViewComponent[];
  [ViewKey.orders]: ViewComponent[];
  [ViewKey.api]: ViewComponent[];
  [ViewKey.shipping_details]: ViewComponent[];
  [ViewKey.pro_plan]: ViewComponent[];
}

export interface OnboardingStructure {
  navigation: NavigationItem[];
  defaultOrdersMechanism: OrdersMechanism;
  viewComponents: ViewComponents;
}

export const OnboardingNavigationOptions = {
  [ViewKey.welcome]: {
    label: 'newOnboarding.nav.welcome',
    route: '/onboarding/welcome',
  },
  [ViewKey.info]: {
    label: 'newOnboarding.nav.info',
    route: '/onboarding/info',
  },
  [ViewKey.preferences]: {
    label: 'newOnboarding.nav.preferences',
    route: '/onboarding/preferences',
  },
  [ViewKey.orders]: {
    label: 'newOnboarding.nav.orders',
    route: '/onboarding/orders',
  },
  [ViewKey.api]: {
    label: 'newOnboarding.nav.api',
    route: '/onboarding/api',
  },
  [ViewKey.getting_started]: {
    label: 'newOnboarding.nav.start',
    route: '/onboarding/getting_started',
  },
  [ViewKey.shipping_details]: {
    label: 'newOnboarding.nav.shipping',
    route: '/onboarding/shipping_details',
  },
  [ViewKey.pro_plan]: {
    label: 'newOnboarding.nav.proPlan',
    route: '/onboarding/pro_plan',
  },
};

// ViewComponents represent a child section/component to the onboarding parent views
export enum ViewComponent {
  basic_information = 'basic_information',
  business_type = 'business_type',
  business_need = 'business_need',
  company_name = 'company_name',
  monthly_volume = 'monthly_volume',
  required_info_help_message = 'required_info_help_message',
  default_label_size = 'default_label_size',
  package_dimensions = 'package_dimensions',
  insurance_settings = 'insurance_settings',
  primary_carrier = 'primary_carrier',
  welcome_message = 'welcome_message',
  api_quick_info = 'api_quick_info',
  api_detail_info = 'api_detail_info',
  order_mechanism = 'order_mechanism',
  store_selection = 'store_selection',
  sender_address = 'sender_address',
  store_confirmation = 'store_confirmation',
  bring_your_own_carrier = 'bring_your_own_carrier',
  master_account_carrier = 'master_account_carrier',
  non_master_account_carrier = 'non_master_account_carrier',
  pro_plan = 'pro_plan',
}

export const includesViewComponent = (
  components: ViewComponent[],
  viewComponent: ViewComponent,
) => {
  return components.indexOf(viewComponent) !== -1;
};

/**
 * These are "sections" within "views" - broken up in a way that they may be conditionally included based business type or need
 */
export const viewComponents: ViewComponents = {
  [ViewKey.info]: [
    ViewComponent.basic_information,
    ViewComponent.business_type,
    ViewComponent.business_need,
    ViewComponent.company_name,
    ViewComponent.monthly_volume,
  ],
  [ViewKey.preferences]: [
    ViewComponent.required_info_help_message,
    ViewComponent.default_label_size,
    ViewComponent.package_dimensions,
    ViewComponent.primary_carrier,
    ViewComponent.sender_address,
    ViewComponent.bring_your_own_carrier,
    ViewComponent.master_account_carrier,
    ViewComponent.non_master_account_carrier,
    ViewComponent.insurance_settings,
  ],
  [ViewKey.getting_started]: [ViewComponent.welcome_message, ViewComponent.api_quick_info],
  [ViewKey.orders]: [
    ViewComponent.order_mechanism,
    ViewComponent.store_selection,
    ViewComponent.sender_address,
    ViewComponent.store_confirmation,
  ],
  [ViewKey.api]: [ViewComponent.api_detail_info],
  [ViewKey.shipping_details]: [ViewComponent.sender_address],
  [ViewKey.pro_plan]: [ViewComponent.pro_plan],
};

/**
 * Get structure of onboarding based on the user-selected type of business and business needs
 * @param type
 * @param businessNeed
 * @param connectedStore
 */
export const getOnboardingStructure = (
  type: BusinessType,
  businessNeed: keyof typeof BusinessNeed | undefined,
  // businessNeed: (keyof BusinessNeed) | undefined,
  connectedStore?: string,
): OnboardingStructure => {
  businessNeed = businessNeed ? businessNeed : 'NONE';

  const structure: OnboardingStructure = {
    navigation: [
      OnboardingNavigationOptions.welcome,
      OnboardingNavigationOptions.info,
      OnboardingNavigationOptions.preferences,
      OnboardingNavigationOptions.pro_plan,
      OnboardingNavigationOptions.orders,
    ],
    defaultOrdersMechanism: OrdersMechanism.MANUAL,
    viewComponents: { ...viewComponents },
  };

  const userCountryStorage = localStorage.getItem(
    OnboardingLocalStorageKeys.ONBOARDING_USER_COUNTRY,
  );
  let isMasterAccountCountry = false;

  if (userCountryStorage) {
    const userCountry = JSON.parse(userCountryStorage);
    isMasterAccountCountry = getMasterAccountDetail(userCountry).hasMasterAccount;
  }

  switch (type) {
    case BusinessType.BUSINESS:
      // @NOTE: Business users will see all preferences components besides the help message
      structure.viewComponents[ViewKey.preferences] = [
        ViewComponent.default_label_size,
        ViewComponent.package_dimensions,
        ViewComponent.bring_your_own_carrier,
        ViewComponent.primary_carrier,
        isMasterAccountCountry
          ? ViewComponent.master_account_carrier
          : ViewComponent.non_master_account_carrier,
        ViewComponent.insurance_settings,
      ];
      // @NOTE: Business users will see all orders components
      structure.viewComponents[ViewKey.orders] = [
        ViewComponent.order_mechanism,
        ViewComponent.store_selection,
        ViewComponent.sender_address,
        ViewComponent.store_confirmation,
      ];
      structure.viewComponents[ViewKey.pro_plan] = [ViewComponent.pro_plan];
      // @NOTE: Normal business users do not see these views, but overridden below for business/api users
      structure.viewComponents[ViewKey.getting_started] = [];
      structure.viewComponents[ViewKey.api] = [];
      structure.viewComponents[ViewKey.shipping_details] = [];
      if (BusinessNeed[businessNeed] === BusinessNeed['Shipping Goods']) {
        structure.defaultOrdersMechanism = OrdersMechanism.STORE;
      } else if (BusinessNeed[businessNeed] === BusinessNeed['Shipping Free Items']) {
        structure.defaultOrdersMechanism = OrdersMechanism.MANUAL;
      } else if (BusinessNeed[businessNeed] === BusinessNeed['Developer']) {
        structure.defaultOrdersMechanism = OrdersMechanism.MANUAL;
        structure.navigation = [
          OnboardingNavigationOptions.welcome,
          OnboardingNavigationOptions.info,
          OnboardingNavigationOptions.preferences,
          OnboardingNavigationOptions.pro_plan,
          OnboardingNavigationOptions.api,
        ];
        // @NOTE: Overridden from above, business/api users will see these
        structure.viewComponents[ViewKey.api] = [ViewComponent.api_detail_info];
        structure.viewComponents[ViewKey.preferences] =
          connectedStore && (connectedStore === 'Shopify' || connectedStore === 'Square')
            ? [
                ViewComponent.default_label_size,
                ViewComponent.primary_carrier,
                ViewComponent.bring_your_own_carrier,
                ViewComponent.insurance_settings,
                isMasterAccountCountry
                  ? ViewComponent.master_account_carrier
                  : ViewComponent.non_master_account_carrier,
              ]
            : [
                ViewComponent.default_label_size,
                ViewComponent.sender_address,
                ViewComponent.primary_carrier,
                ViewComponent.bring_your_own_carrier,
                ViewComponent.insurance_settings,
                isMasterAccountCountry
                  ? ViewComponent.master_account_carrier
                  : ViewComponent.non_master_account_carrier,
              ];
        // @NOTE: Business/API users do not see these views
        structure.viewComponents[ViewKey.orders] = [];
        structure.viewComponents[ViewKey.shipping_details] = [];
      }
      break;
    case BusinessType.PERSONAL:
      // @NOTE: Personal should follow the default
      // @NOTE: business_need, company_name, and monthly_volume hidden for personal
      structure.viewComponents[ViewKey.info] = [
        ViewComponent.basic_information,
        ViewComponent.business_type,
      ];
      // @NOTE: Shipping preferences view includes a help message and hidden package templates section
      structure.viewComponents[ViewKey.preferences] = [
        ViewComponent.required_info_help_message,
        ViewComponent.default_label_size,
        ViewComponent.primary_carrier,
        isMasterAccountCountry
          ? ViewComponent.master_account_carrier
          : ViewComponent.non_master_account_carrier,
        ViewComponent.insurance_settings,
      ];
      // @NOTE: Personal users will see "sender address" (aka shipping details) instead of "add orders"
      structure.viewComponents[ViewKey.shipping_details] = [ViewComponent.sender_address];
      // @NOTE: Personal users do not see these views
      structure.viewComponents[ViewKey.orders] = [];
      structure.viewComponents[ViewKey.getting_started] = [];
      structure.viewComponents[ViewKey.api] = [];

      structure.navigation = [
        OnboardingNavigationOptions.welcome,
        OnboardingNavigationOptions.info,
        OnboardingNavigationOptions.preferences,
        OnboardingNavigationOptions.pro_plan,
        OnboardingNavigationOptions.shipping_details,
      ];

      break;
    case BusinessType.ADVANCED:
      if (
        BusinessNeed[businessNeed] === BusinessNeed['3PL'] ||
        BusinessNeed[businessNeed] === BusinessNeed.XMS ||
        BusinessNeed[businessNeed] === BusinessNeed.Warehouse ||
        BusinessNeed[businessNeed] === BusinessNeed['eCommerce Platform'] ||
        BusinessNeed[businessNeed] === BusinessNeed.Marketplace ||
        BusinessNeed[businessNeed] === BusinessNeed['Seeking 3PL / Inventory Partner'] ||
        BusinessNeed[businessNeed] === BusinessNeed['Seeking WMS']
      ) {
        structure.navigation = [
          OnboardingNavigationOptions.welcome,
          OnboardingNavigationOptions.info,
          OnboardingNavigationOptions.pro_plan,
          OnboardingNavigationOptions.getting_started,
        ];
        // @NOTE: Advanced/partner see the getting started view after info
        structure.viewComponents[ViewKey.getting_started] = [
          ViewComponent.welcome_message,
          ViewComponent.api_quick_info,
        ];
        // @NOTE: Advanced/partner users do not see these views
        structure.viewComponents[ViewKey.shipping_details] = [];
        structure.viewComponents[ViewKey.preferences] = [];
        structure.viewComponents[ViewKey.orders] = [];
        structure.viewComponents[ViewKey.api] = [];
      } else if (BusinessNeed[businessNeed] === BusinessNeed.Developer) {
        structure.navigation = [
          OnboardingNavigationOptions.welcome,
          OnboardingNavigationOptions.info,
          OnboardingNavigationOptions.preferences,
          OnboardingNavigationOptions.pro_plan,
          OnboardingNavigationOptions.api,
        ];
        // @NOTE: Overridden from above, advanced/api users will see these
        structure.viewComponents[ViewKey.api] = [ViewComponent.api_detail_info];
        // @NOTE: Still see a sender address on the preferences view
        structure.viewComponents[ViewKey.preferences] = [
          ViewComponent.default_label_size,
          ViewComponent.sender_address,
          ViewComponent.primary_carrier,
          ViewComponent.bring_your_own_carrier,
          isMasterAccountCountry
            ? ViewComponent.master_account_carrier
            : ViewComponent.non_master_account_carrier,
          ViewComponent.insurance_settings,
        ];
        // @NOTE: Advanced/API users do not see these views
        structure.viewComponents[ViewKey.shipping_details] = [];
        structure.viewComponents[ViewKey.getting_started] = [];
        structure.viewComponents[ViewKey.orders] = [];
      } else {
        // @NOTE: Advanced/other sees a normal flow
        structure.viewComponents[ViewKey.preferences] = [
          ViewComponent.default_label_size,
          ViewComponent.package_dimensions,
          ViewComponent.primary_carrier,
          ViewComponent.bring_your_own_carrier,
          isMasterAccountCountry
            ? ViewComponent.master_account_carrier
            : ViewComponent.non_master_account_carrier,
          ViewComponent.insurance_settings,
        ];
        structure.viewComponents[ViewKey.orders] = [
          ViewComponent.order_mechanism,
          ViewComponent.store_selection,
          ViewComponent.sender_address,
          ViewComponent.store_confirmation,
        ];
        structure.viewComponents[ViewKey.getting_started] = [];
        structure.viewComponents[ViewKey.api] = [];
        structure.viewComponents[ViewKey.shipping_details] = [];
      }
      break;
    default:
      break;
  }

  // Handle if store is already connected
  if (
    connectedStore &&
    (type === BusinessType.BUSINESS || type === BusinessType.PERSONAL) &&
    BusinessNeed[businessNeed] !== BusinessNeed.Developer
  ) {
    structure.viewComponents[ViewKey.orders] = [];
    // @NOTE: For store-initiated registrations that aren't Shopify/Square, show a "sender address" item in menu
    if (connectedStore !== 'Shopify' && connectedStore !== 'Square') {
      structure.viewComponents[ViewKey.shipping_details] = [ViewComponent.sender_address];
      structure.navigation = [
        OnboardingNavigationOptions.welcome,
        OnboardingNavigationOptions.info,
        OnboardingNavigationOptions.preferences,
        OnboardingNavigationOptions.pro_plan,
        OnboardingNavigationOptions[ViewKey.shipping_details],
      ];
    } else {
      // @NOTE: For store-initiated registration for stores that automatically sync sender address
      structure.viewComponents[ViewKey.shipping_details] = [];
      structure.navigation = [
        OnboardingNavigationOptions.welcome,
        OnboardingNavigationOptions.info,
        OnboardingNavigationOptions.preferences,
        OnboardingNavigationOptions.pro_plan,
      ];
    }
  }

  return structure;
};

export const resetOnboardingPersistence = () => {
  Object.keys(OnboardingLocalStorageKeys).forEach((key) => {
    localStorage.removeItem(
      OnboardingLocalStorageKeys[key as keyof typeof OnboardingLocalStorageKeys],
    );
  });
};

export const countryHasMasterAccount = (country: Country) => {
  const masterAccountCountries = ['US', 'AU', 'DE', 'CA'];
  return masterAccountCountries.indexOf(country.iso2) !== -1;
};

export const referralStores: string[] = [
  StoreName.Shopify,
  StoreName.Wix,
  StoreName.Godaddy,
  StoreName.Square,
  StoreName.Weebly,
];
